import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import Constant from "../../../utils/constant";
import Breadcrumb from "../../shared/common/breadcrumb";
import { IBreadcrumb } from "../../../model/interface/breadcrumb";
import { useForm } from "react-hook-form";
import { IGrade } from "../../../model/interface/grade";
import { toast } from "react-toastify";
import Loader from "../../shared/loader/loader";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import { useNumericParam } from "../../../utils/useNumericParam";

const ManageGrade: React.FC<any> = () => {
  const navigate = useNavigate();
  const id = useNumericParam("id");
  const [gradeToEdit, setGradeToEdit] = useState<IGrade>();
  const [loading, setLoading] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    (async () => {
      const gradeId = id;
      if (gradeId === 0) {
        navigate(Constant.BAD_REQUEST, { replace: true });
      } else if (gradeId) {
        setLoading(true);
        const service = await import("../../../api/grade.service");
        const response = await service.getGradeById(gradeId);
        setLoading(false);
        setGradeToEdit(response.data);
      }
    })();
  }, []);

  const onSubmit = async (data) => {
    const content: IGrade = {
      gradeId: gradeToEdit ? gradeToEdit.gradeId : 0,
      name: data.name,
      abbreviation: data.abbreviation,
      isActive: data.isActive,
    };

    const service = await import("../../../api/grade.service");
    gradeToEdit
      ? service.updateGradeSection(content).then(() => {
          toast.success("Grade updated successfully");
          setTimeout(() => {
            navigate(Constant.GRADES);
          }, 1500);
        })
      : service.postGrade(content).then(() => {
          toast.success("Grade added successfully");
          setTimeout(() => {
            navigate(Constant.GRADES);
          }, 1500);
        });
  };

  const breadcrumbItems = () => {
    const items: Array<IBreadcrumb> = [
      { name: "Grade", url: Constant.GRADES },
      { name: gradeToEdit ? "Edit Grade" : "Add Grade", url: "" },
    ];

    if (gradeToEdit?.gradeId) {
      items.push({ name: gradeToEdit.gradeId.toString(), url: "" });
    }
    return items;
  };

  return (
    <AllowedTo
      perform={[Permissions.grade_create, Permissions.grade_modify]}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}

      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 overflow-y-auto mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">
                {gradeToEdit ? "Edit Grade " : "Add Grade "}
              </h3>
            </div>
            <Breadcrumb items={breadcrumbItems()} />
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="shadow py-6 border rounded-lg mt-4 px-4 w-1/4">
                <div>
                  <div className="grid grid-cols-1">
                    <div className="text-sm">
                      <label className="font-medium text-gray-700">
                        Name <span className="text-red-600">*</span>
                      </label>
                    </div>
                    <div>
                      <input
                        type="text"
                        defaultValue={gradeToEdit?.name}
                        autoComplete="off"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        {...register("name", {
                          required: true,
                          value: gradeToEdit?.name,
                        })}
                      />
                      {errors.name && (
                        <span className="text-red-700 inline-block pt-1 text-sm">
                          Name is required.
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 mt-5">
                    <div className="text-sm">
                      <label className="font-medium text-gray-700">
                        Abbreviation{" "}
                        <span className="text-red-600 text-sm">*</span>
                      </label>
                    </div>
                    <div>
                      <input
                        type="text"
                        defaultValue={gradeToEdit && gradeToEdit.abbreviation}
                        autoComplete="off"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        {...register("abbreviation", {
                          required: true,
                          maxLength: 2,
                          value: gradeToEdit?.abbreviation,
                        })}
                      />
                      {errors.abbreviation &&
                        errors.abbreviation.type === "required" && (
                          <span className="pt-1 inline-block text-red-700 text-sm">
                            Abbreviation is required.
                          </span>
                        )}
                      {errors.abbreviation &&
                        errors.abbreviation.type === "maxLength" && (
                          <span className="text-red-700 text-sm inline-block pt-1">
                            Abbreviation must not be more than 2 characters.
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="flex justify-end mt-8 items-center border-t ">
                    <button
                      type="submit"
                      className="inline-flex items-center px-3 py-2 my-3 hover:bg-secondary-color border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white dark:text-white bg-primary-color  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                    >
                      {gradeToEdit ? "Update " : "Save"}
                    </button>
                    <Link to={Constant.GRADES} className="ml-2">
                      <button className="inline-flex items-center px-3 py-2 my-3 hover:text-gray-500 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color">
                        Cancel
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

export default ManageGrade;
